import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Stack,
    Alert,
    Box,
    IconButton,
    InputAdornment,
} from '@mui/material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../context/NotificationContext';
import { requestPasswordReset } from '../api';
import logoLarge from '../mfs-logo.png';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login, user, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [infoMessage, setInfoMessage] = useState(location.state?.sessionExpired ? 'Your session ended due to inactivity. Please sign in again.' : '');
    const { showNotification } = useNotification();
    const [forgotLoading, setForgotLoading] = useState(false);
    const theme = useTheme();
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            navigate('/dashboard', { replace: true });
        }
    }, [user, loading, navigate]);

    useEffect(() => {
        if (location.state?.sessionExpired) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    if (!loading && user) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); // Clear previous errors
        setInfoMessage('');
        try {
            await login(email, password);
            navigate('/dashboard'); // Redirect to dashboard on successful login
        } catch (err) {
            setError('Failed to log in. Please check your credentials.');
            console.error(err);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError('Enter your email above to receive a reset link.');
            return;
        }
        try {
            setError('');
            setForgotLoading(true);
            await requestPasswordReset(email);
            showNotification({
                message: 'If that email is registered, we sent a password reset link.',
                severity: 'info',
                autoHideDuration: 6000,
            });
        } catch (err) {
            console.error('Failed to request password reset:', err);
            setError('Could not send reset email. Please try again.');
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs" sx={{ mt: 8 }}>
            <Paper elevation={6} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                    component="img"
                    src={logoLarge}
                    alt="Mister Food Safety"
                    sx={{
                        height: 56,
                        mb: 2,
                        objectFit: 'contain',
                        filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                        transition: 'filter 0.2s ease',
                    }}
                />
                <Typography component="h1" variant="h5">
                    Sign In
                </Typography>
                {infoMessage && <Alert severity="info" sx={{ width: '100%', mt: 2 }}>{infoMessage}</Alert>}
                {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}
                <Stack component="form" onSubmit={handleSubmit} spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 1 }}
                    >
                        Sign In
                    </Button>
                    <Button
                        type="button"
                        variant="text"
                        onClick={handleForgotPassword}
                        disabled={forgotLoading}
                        sx={{ alignSelf: 'flex-end' }}
                    >
                        {forgotLoading ? 'Sending reset link…' : 'Forgot password?'}
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
};

export default LoginPage;
